import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { Check, Loader2 } from "lucide-react";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";

type SettingsSaveFullscreenModalProps = {
  open: boolean;
  savePhase: number;
  savePhases: readonly number[];
};

export function SettingsSaveFullscreenModal({
  open,
  savePhase,
  savePhases,
}: SettingsSaveFullscreenModalProps) {
  const vt = useVisibleT();

  return (
    <Dialog open={open}>
      <DialogContent
        overlay="full"
        needCloseButton={false}
        preventOutsideClose
        onEscapeKeyDown={(e) => e.preventDefault()}
        className="z-[120] max-w-2xl border-0 bg-transparent p-0 shadow-none"
        aria-describedby="settings-save-progress-description"
      >
        <DialogTitle className="sr-only">
          {vt(
            "settings.general.savingProgress.dialogTitle",
            "Enregistrement des réglages",
          )}
        </DialogTitle>
        <DialogDescription
          id="settings-save-progress-description"
          className="sr-only"
        >
          {vt(
            "settings.general.savingProgress.dialogDescription",
            "Veuillez patienter pendant l'enregistrement de vos réglages",
          )}
        </DialogDescription>
        <div className="mx-auto flex w-full max-w-md flex-col items-center gap-5 px-4 text-slate-50 drop-shadow-md">
          <Loader2
            className="h-10 w-10 shrink-0 animate-spin text-brand-300"
            aria-hidden
          />
          <ol className="w-full space-y-3 text-left text-sm leading-relaxed">
            {savePhases.map((step) => {
              const done = savePhase > step;
              const active = savePhase === step;
              return (
                <li
                  key={step}
                  className={cn(
                    "flex gap-3 transition-[opacity,color] duration-200",
                    done && "text-slate-300/90",
                    active && "font-medium text-white",
                    !done && !active && "text-slate-400/70",
                  )}
                >
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center">
                    {done ? (
                      <Check
                        className="h-4 w-4 text-emerald-300"
                        strokeWidth={2.5}
                        aria-hidden
                      />
                    ) : active ? (
                      <Loader2
                        className="h-4 w-4 animate-spin text-brand-300"
                        aria-hidden
                      />
                    ) : (
                      <span className="h-1.5 w-1.5 rounded-full bg-current opacity-50" />
                    )}
                  </span>
                  <span>
                    {step === 1
                      ? vt(
                          "settings.general.savingProgress.step1",
                          "On enregistre vos paramètres",
                        )
                      : step === 2
                        ? vt(
                            "settings.general.savingProgress.step2",
                            "Nous sommes en train de mettre à jour les noms de vos champs dans toute l'application",
                          )
                        : vt(
                            "settings.general.savingProgress.step3",
                            "Merci pour votre patience",
                          )}
                  </span>
                </li>
              );
            })}
          </ol>
        </div>
      </DialogContent>
    </Dialog>
  );
}
